<template>
  <div class="filter-dropdown" ref="el">
    <div class="filter-dropdown__input">
      <div class="form__element form__element--floating">
        <div class="form__input"
             ref="input"
            :class="{
          'form__input--disabled': disabled,
          'form__input--negative': inverted,
          'form__input--error': hasError}"
            :inert="disabled">
          <div class="form__input-icon">
            <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-hidden="true" focusable="false">
                <path fill-rule="evenodd" d="M16.333 8a8.333 8.333 0 1 0 5.83 14.288l.005-.005A8.333 8.333 0 0 0 16.333 8Zm7.93 14.96a10.292 10.292 0 0 0 2.404-6.627C26.667 10.626 22.04 6 16.333 6S6 10.626 6 16.333s4.626 10.334 10.333 10.334a10.29 10.29 0 0 0 6.504-2.303l3.468 3.355a1 1 0 0 0 1.39-1.438l-3.433-3.32Z" clip-rule="evenodd"></path>
              </svg>
            </span>
          </div>
          <div class="form__input-main-wrapper">
            <input type="hidden" name="studio" autocomplete="off" :value="hiddenValue" />
            <input class="form__input-field"
                   type="text"
                   :id="'select-location-finder-' + inputId + '-' + id"
                   :placeholder="computedLabel"
                   role="combobox"
                   :value="modelValue"
                   @focus="handleFocus"
                   @input="handleInput"
                   @blur="handleBlur"
                   :disabled="disabled"
                   :aria-invalid="hasError ? 'true' : null"
                   :aria-describedby="hasError ? `error-location-finder-'${inputId}'-'${id}` : null"
                   aria-haspopup="listbox"
                   autocomplete="off"
                   :aria-expanded="visibleOptions"
                   :aria-controls="'select-location-finder-options' + inputId + '-' + id">
            <span class="form__label">
                <label class="label" :for="'select-location-finder-' + inputId + '-' + id">{{ computedLabel }}</label>
            </span>
            <button class="btn form__input-clear-btn btn--icon-only"
                    v-if="modelValue && modelValue.length > 0"
                    @click="clearInput"
                    :aria-label="t('locationfinder.clear')" type="button">
              <span class="btn__icon">
                <span class="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" role="presentation">
                    <path d="M16.238 17 9 9.762 9.762 9 17 16.238 24.238 9l.762.762L17.762 17 25 24.238l-.762.762L17 17.762 9.762 25 9 24.238 16.238 17Z"></path>
                  </svg>
                </span>
              </span>
            </button>
          </div>
          <button class="btn btn--icon-only" ref="toggle" @click="visibleOptions = !visibleOptions" :aria-label="visibleOptions ? t('locationfinder.close') : t('locationfinder.open')" :aria-controls="'select-location-finder-options' + inputId + '-' + id" type="button">
              <span class="btn__icon">
                <span class="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-hidden="true" focusable="false" role="presentation">
                      <path d="m16.507 18.68 6.508-6.18 1.485 1.41-7.993 7.59L8.5 13.91 10 12.5l6.507 6.18Z"></path>
                    </svg>
                </span>
              </span>
          </button>
        </div>
        <Error v-if="hasError"
               :id="'error-location-finder-' + inputId + '-' + id"
               :message="errorMessage" />
      </div>
    </div>
    <div class="filter-dropdown__options"
         :class="{'filter-dropdown__options--show': visibleOptions}"
         :id="'select-location-finder-options' + inputId + '-' + id" role="listbox">
      <ul class="filter-dropdown__list" ref="list" :aria-labelledby="'select-location-finder-' + inputId + '-' + id">
        <li class="filter-dropdown__item"
            v-if="filteredStudios.length"
            v-for="(option, index) in filteredStudios"
            :key="option.value">
          <a :href="option.detail_url" :aria-label="t('locationfinder.visit') + ' Studio ' + option.name" v-if="!select" class="filter-dropdown__link">
            <strong>Studio {{ option.name }}</strong><br>
            {{ option.street }} / {{ option.zip }} {{ option.city }}
          </a>
          <a href="#" @click.prevent="handleClick(option)" v-else class="filter-dropdown__link">
            <strong>Studio {{ option.name }}</strong><br>
            {{ option.street }} / {{ option.zip }} {{ option.city }}<br v-if="disableBiaStudios && !option.bia">
            <span style="display: inline-block; margin-top: var(--spacing-8);" v-if="disableBiaStudios && !option.bia" class="badge badge--accent">{{ t('locationfinder.nobia') }}</span>
          </a>
        </li>
        <li class="filter-dropdown__item" v-else><span class="filter-dropdown__link">{{ t('locationfinder.error') }}</span></li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import {computed, onMounted, ref, shallowRef, useId, useTemplateRef, watch} from 'vue';
import {
  getCookie,
  getCountryFromUrl,
  getCountryId,
  getURLParameterByName
} from '../../../../abstracts/utilities.js';
import Error from './Error.vue';
import {googleMapsApiLoader} from '../../../google-maps/utilities.js';

const { t } = useI18n();
const el = useTemplateRef('el');
const toggle = useTemplateRef('toggle');
const list = useTemplateRef('list');
const input = useTemplateRef('input');

const inputId = useId();
const countryId = getCountryId();

const isTouched = ref(false);
const errorMessage = ref('');

const props = defineProps({
  id: {
    type: String,
    default: '1'
  },
  modelValue: {
    type: String,
    default: ''
  },
  inverted: {
    type: Boolean,
    default: false
  },
  disableBiaStudios: {
    type: Boolean,
    default: false
  },
  select: {
    type: Boolean,
    default: true
  },
  studios: {
    type: Object, Array
  },
  rules: {
    type: Array,
    default: () => [],
  },
  forceValidation: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:modelValue', 'validation-change']);

const filteredStudios = shallowRef(props.studios instanceof Array ? props.studios : Object.values(props.studios));
const disabled = !filteredStudios.value || !filteredStudios.value.length;

const internalValue = ref(props.modelValue);
const selectedOption = ref(null);

const hiddenValue = computed(() => {
  if(!!selectedOption) selectedOption.id;
});
const consent = computed(() => {
  return window.uc_googlemaps;
});

onMounted(() => {
  filterStudiosOnMount();

  let urlPath = window.location.pathname,
      studioByParam = '';

  urlPath = urlPath.split('/');

  if(props.select) {

    if(getURLParameterByName('studio')?.length) {
      // handle studio url parameter
      studioByParam = getStudioByUid(getURLParameterByName('studio'));
    } else if(urlPath.indexOf('_studio') > 0) {
      // handle studio url path
      studioByParam = getStudioByUid(urlPath[urlPath.indexOf('_studio') + 1]);
    } else if(getCookie('kieserstudio_studio')) {
      // handle studio cookie
      studioByParam = getStudioByUid(getCookie('kieserstudio_studio'));
    }
  }

  if(!studioByParam.length) return;

  internalValue.value = studioByParam[0].name;
  selectedOption.value = studioByParam[0];
  emit('update:modelValue', studioByParam[0].name);
});

const visibleOptions = ref(false);
const computedLabel = computed(() => consent.value ? t('locationfinder.select.labelGoogleMaps') : t('locationfinder.select.label'));

watch(() => props.modelValue, (newValue) => {
  internalValue.value = newValue;

  if(!newValue.length) {
    // reset on empty input
    filteredStudios.value = props.studios instanceof Array ? props.studios : Object.values(props.studios);
    filterStudiosOnMount();
  }

  // filter by input
  if(newValue.length >= 3) {
    googleMapsApiLoader(consent.value, (loader) => {

      loader.load().then(async () => {
        const {AutocompleteService} = await google.maps.importLibrary("places");

        // autocomplete options
        let options = {
          input: newValue,
          types: ['(regions)'],
          componentRestrictions: {country: getCountryFromUrl()},
        };

        // get valid address data from user input
        const autocomplete = new AutocompleteService();
        autocomplete.getPlacePredictions(options, (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            // get lat,lng values for prediction
            const geocoder = new google.maps.Geocoder;
            geocoder.geocode({'address': predictions[0].description}, function(results, status) {
              if (status === 'OK' && results[0]) {
                // calculate distance between prediction and studio
                filteredStudios.value.forEach((studio) => {
                  let meters = google.maps.geometry.spherical.computeDistanceBetween(
                      new google.maps.LatLng(studio.lat, studio.lng),
                      new google.maps.LatLng(results[0].geometry.location.lat(), results[0].geometry.location.lng()));

                  studio.distance = (meters / 1000).toFixed(1); // in km
                });

                // sort by distance
                filteredStudios.value = filteredStudios.value.sort(function(a, b) {
                  return a.distance - b.distance;
                });
              }
            });
          }

          // handle zero results
          if(status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS || status === google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR) {
            filteredStudios.value = [];
          }
        });
      });
    }, (err) => {
      console.warn(err);

      // filter by input
      filteredStudios.value = filteredStudios.value.filter((option) => (option.name.toLowerCase().indexOf(newValue.toLowerCase()) > -1));
    })
  }
});

watch(() => visibleOptions.value, (newValue) => {
  if(newValue) {
    let inputBottom = input.value.getBoundingClientRect().top + input.value.offsetHeight,
        windowBottom = window.innerHeight;

    // set select dropdown max height
    list.value.style.maxHeight = 'calc(' + windowBottom + 'px - ' + inputBottom + 'px - (var(--spacing-24) * 2))';
    list.value.style.overflow = 'auto';
  }
});

const validate = () => {
  for (const rule of props.rules) {
    const result = rule(internalValue.value);

    if (typeof result === 'string') {
      errorMessage.value = result;
      emit('validation-change', false);
      return false;
    }
  }
  errorMessage.value = '';
  emit('validation-change', true);
  return true;
};

const filterStudiosOnMount = () => {
  // prefilter studios by country if needed
  if(!!filteredStudios.value[0]?.country) {
    filteredStudios.value = filteredStudios.value.filter((option) => option.country == countryId);
  }
  // sort prefiltered studios by name
  filteredStudios.value = filteredStudios.value.sort(function(a, b) {
    let nameA = a.name,
        nameB = b.name;
    return nameA < nameB ? -1 : (nameA > nameB ? 1 : 0);
  });
}

watch([internalValue, () => props.forceValidation, isTouched], () => {
  if (isTouched.value || props.forceValidation) {
    validate();
  } else {
    errorMessage.value = '';
  }
}, { immediate: true });

const handleFocus = (event) => {
  visibleOptions.value = true;
};

const handleInput = (event) => {
  internalValue.value = event.target.value;
  emit('update:modelValue', internalValue.value);
};

const handleBlur = (event) => {
  isTouched.value = true;
  validate();
};

const handleClick = (option) => {
  internalValue.value = option.name;
  emit('update:modelValue', internalValue.value);

  visibleOptions.value = false;
};

const clearInput = () => {
  internalValue.value = '';
  emit('update:modelValue', '');
};

const getStudioByUid = (id) => {
  let studios = props.studios instanceof Array ? props.studios : Object.values(props.studios)
  return studios.filter((option) => option.id == id);
}

const hasError = computed(() => (isTouched.value || props.forceValidation) && errorMessage.value !== '');

</script>